import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import { Activity } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import MmcFile from "@mapmycustomers/shared/types/File";
import User, { UserRef } from "@mapmycustomers/shared/types/User";

import { RecurringActionType } from "@app/component/createEditEntity/Activity/enum/RecurringActionType";
import { EntityMention } from "@app/component/EntityMentions";
import CoachingNote from "@app/scene/dashboard/types/CoachingNote";
import { SuggestedActionRecord } from "@app/scene/dashboard/utils/parseSuggestedActions";

export type CoachingTasksFilter = "active" | "all" | "completed";

export type CoachingTasksSort = "dueDate-asc" | "dueDate-desc" | "updatedAt-asc" | "updatedAt-desc";

export interface FetchCoachingTasksRequest {
  append?: boolean;
  crmActivityTypeId: ActivityType["id"];
  filter: CoachingTasksFilter;
  limit: number;
  offset: number;
  silent?: boolean;
  sortBy: CoachingTasksSort;
  tabUserId: User["id"];
}

export const fetchCoachingTasks = createAsyncAction(
  "@dashboard/coaching/tasks/fetch/request",
  "@dashboard/coaching/tasks/fetch/success",
  "@dashboard/coaching/tasks/fetch/failure"
)<
  FetchCoachingTasksRequest,
  { append: boolean; silent: boolean; tasks: Activity[]; total: number },
  void
>();

export interface CreateCoachingTaskRequest {
  assigneeId: User["id"];
  coachingNoteId?: CoachingNote["id"];
  crmActivityTypeId: ActivityType["id"];
  dueDate: null | string;
  fileIds?: Array<MmcFile["id"]>;
  // In-session only — Activity API doesn't persist mentions; lost on reload.
  mentions?: EntityMention[];
  name: string;
  // Files dragged into the AddTaskModal that aren't yet in the rep's pool.
  // The saga uploads them via createCoachingFile (so they land in the pool +
  // appear in the panel) and folds their resulting `fileId`s into the task's
  // crm_activities.files alongside whatever the picker selected. Without this,
  // dropped-in files only ended up in the pool and the task showed no chip.
  newFiles?: Array<Blob | File>;
  note?: string;
  onSuccess?: () => void;
  subjectUserId: User["id"];
}

export const createCoachingTask = createAsyncAction(
  "@dashboard/coaching/tasks/create/request",
  "@dashboard/coaching/tasks/create/success",
  "@dashboard/coaching/tasks/create/failure"
)<CreateCoachingTaskRequest, Activity, void>();

export interface CreateCoachingTasksForTeamRequest {
  // One individual task is created per assignee (subjectUserId = assigneeId).
  assigneeIds: User["id"][];
  crmActivityTypeId: ActivityType["id"];
  dueDate: null | string;
  fileIds?: Array<MmcFile["id"]>;
  // Same in-session-only contract as the single-create path — seeded per created task.
  mentions?: EntityMention[];
  name: string;
  // Uploaded to each assignee's own pool before their task is created.
  newFiles?: Array<Blob | File>;
  note?: string;
  // Receives the number of tasks the bulk endpoint actually created.
  onSuccess?: (createdCount: number) => void;
}

export const createCoachingTasksForTeam = createAsyncAction(
  "@dashboard/coaching/tasks/createForTeam/request",
  "@dashboard/coaching/tasks/createForTeam/success",
  "@dashboard/coaching/tasks/createForTeam/failure"
)<CreateCoachingTasksForTeamRequest, Activity[], void>();

export interface FetchSuggestedActionOwnerRequest {
  record?: SuggestedActionRecord;
}

export const fetchSuggestedActionOwner = createAsyncAction(
  "@dashboard/coaching/tasks/fetchSuggestedActionOwner/request",
  "@dashboard/coaching/tasks/fetchSuggestedActionOwner/success",
  "@dashboard/coaching/tasks/fetchSuggestedActionOwner/failure"
)<FetchSuggestedActionOwnerRequest, null | UserRef, void>();

export interface UpdateCoachingTaskRequest {
  activity: Activity;
  dueDate?: null | string;
  fileIds?: Array<MmcFile["id"]>;
  // Same in-session-only contract as the create path — see CreateCoachingTaskRequest.
  mentions?: EntityMention[];
  name?: string;
  // Files dropped in during edit. The saga uploads them to the rep's pool
  // first then folds the resulting platform fileIds into the activity's
  // crm_activities.files alongside `fileIds`, mirroring the create flow.
  newFiles?: Array<Blob | File>;
  note?: string;
}

export const updateCoachingTask = createAsyncAction(
  "@dashboard/coaching/tasks/update/request",
  "@dashboard/coaching/tasks/update/success",
  "@dashboard/coaching/tasks/update/failure"
)<UpdateCoachingTaskRequest, Activity, Activity["id"]>();

export const toggleCoachingTaskComplete = createAsyncAction(
  "@dashboard/coaching/tasks/toggleComplete/request",
  "@dashboard/coaching/tasks/toggleComplete/success",
  "@dashboard/coaching/tasks/toggleComplete/failure"
)<Activity, Activity, Activity["id"]>();

export const deleteCoachingTask = createAsyncAction(
  "@dashboard/coaching/tasks/delete/request",
  "@dashboard/coaching/tasks/delete/success",
  "@dashboard/coaching/tasks/delete/failure"
)<
  { deleteRecurring?: RecurringActionType; id: Activity["id"] },
  { id: Activity["id"] },
  Activity["id"]
>();

// Bumps a counter in state so the panel's existing fetch useEffect re-runs
export const bumpTasksRefreshCounter = createAction(
  "@dashboard/coaching/tasks/bumpRefreshCounter"
)();

// Seeds the in-session mentions map for a task.
export const setCoachingTaskMentions = createAction("@dashboard/coaching/tasks/setMentions")<{
  mentions: EntityMention[];
  taskId: Activity["id"];
}>();

// Opens the AddTaskModal in "edit existing task" mode.
export const openCoachingTaskEdit = createAction("@dashboard/coaching/tasks/openEdit")<
  Activity["id"]
>();

// Reducer-only — used by the saga to inject a freshly-fetched activity into
export const injectCoachingTask = createAction("@dashboard/coaching/tasks/inject")<Activity>();

export const fetchLinkedTasks = createAsyncAction(
  "@dashboard/coaching/tasks/fetchLinked/request",
  "@dashboard/coaching/tasks/fetchLinked/success",
  "@dashboard/coaching/tasks/fetchLinked/failure"
)<{ taskIds: Activity["id"][] }, Activity[], void>();

export const closeCoachingTaskEdit = createAction("@dashboard/coaching/tasks/closeEdit")();

export type CoachingTasksActions = ActionType<
  | typeof bumpTasksRefreshCounter
  | typeof closeCoachingTaskEdit
  | typeof createCoachingTask
  | typeof createCoachingTasksForTeam
  | typeof deleteCoachingTask
  | typeof fetchCoachingTasks
  | typeof fetchLinkedTasks
  | typeof fetchSuggestedActionOwner
  | typeof injectCoachingTask
  | typeof openCoachingTaskEdit
  | typeof setCoachingTaskMentions
  | typeof toggleCoachingTaskComplete
  | typeof updateCoachingTask
>;
